import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Plus,
  Trash2,
  Save,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { CustomerSearchModal } from '@components/CustomerSearchModal';
import { DateInputBR } from '@components/DateInputBR';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { AddressCepNotice } from '@components/AddressCepNotice';
import { ConfirmDialog } from '@components/Dialog';
import { salesOrderService } from '../../services/salesOrderService';
import { productService } from '../../services/productService';
import { companyService } from '../../services/companyService';
import { Company, formatCnpj } from '../../types/company';
import { Product } from '../../types/product';
import { Customer, SalesOrder, SALES_ORDER_MANUAL_STATUS_OPTIONS, toManualStatusOptions, OrderStatusId, ItemPayload, InstallmentPayload, OrderPaymentMethod, Seller } from '../../types/salesOrder';
import { useAddressCepCheck } from '../../hooks/useAddressCepCheck';
import { applyCepToDivergent } from '../../utils/addressCepCheck';
import { normalizeCep, shippingAddressError } from './salesOrderAddress';
import { orderTypeService } from '../../services/orderTypeService';
import { OrderType } from '../../types/orderType';

const PAYMENT_OPTIONS = [
  'Cartão de Crédito a Vista',
  'Cartão de Crédito Parcelado',
  'PIX',
  'Transferência Bancária',
];

// ─── Local types ──────────────────────────────────────────────────

interface FormItem extends ItemPayload {
  _tempId: number;
  is_gift?: boolean;
}

interface FormInstallment extends InstallmentPayload {
  _tempId: number;
}

// ─── Helpers ──────────────────────────────────────────────────────

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDocument = (doc?: string) => {
  if (!doc) return '';
  const d = doc.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

let _tempSeq = 1;
const nextTempId = () => _tempSeq++;

// ─── Component ────────────────────────────────────────────────────

export const SalesOrderFormView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Clone source ──────────────────────────────────────────────
  const cloneSource = (location.state as { cloneSource?: SalesOrder })?.cloneSource ?? null;
  const isClone = Boolean(cloneSource);

  // ── General state ─────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  // Erro exibido em modal. Guarda o valor bruto do `catch` para que o
  // `ApiErrorModal` possa ler os erros por campo de um `ApiError`; validações
  // locais entram como string mesmo.
  const [error, setError] = useState<unknown>(null);
  const [companies, setCompanies] = useState<Company[]>([]);

  // ── Form header fields ────────────────────────────────────────
  const [companyId, setCompanyId] = useState<number | ''>(cloneSource?.company_id ?? '');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [orderStatus, setOrderStatus] = useState(OrderStatusId.EmAberto);
  const [seller, setSeller] = useState(cloneSource?.seller ?? '');
  const [observation, setObservation] = useState(cloneSource?.observation ?? '');
  const [internalObs, setInternalObs] = useState(cloneSource?.internal_observation ?? '');
  const [isPriority, setIsPriority] = useState(cloneSource?.is_priority ?? false);
  const [requestDeliveryTime, setRequestDeliveryTime] = useState(cloneSource?.request_delivery_time ?? '');
  const [orderTypeId, setOrderTypeId] = useState<number | ''>(cloneSource?.order_type_id ?? '');
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);

  // ── Customer search ───────────────────────────────────────────
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    cloneSource?.customer_id
      ? { customer_id: cloneSource.customer_id, name: cloneSource.customer_name ?? '', document: cloneSource.customer_document ?? '' } as Customer
      : null,
  );

  // ── Shipping address ──────────────────────────────────────────
  const [addrStreet, setAddrStreet] = useState(cloneSource?.ship_addr_street ?? '');
  const [addrNum, setAddrNum] = useState(cloneSource?.ship_addr_num ?? '');
  const [addrCompl, setAddrCompl] = useState(cloneSource?.ship_addr_compl ?? '');
  const [addrDistrict, setAddrDistrict] = useState(cloneSource?.ship_addr_district ?? '');
  const [addrCity, setAddrCity] = useState(cloneSource?.ship_addr_city ?? '');
  const [addrState, setAddrState] = useState(cloneSource?.ship_addr_state ?? '');
  const [addrZip, setAddrZip] = useState(cloneSource?.ship_addr_zip ?? '');

  // ── Shipping info ─────────────────────────────────────────────
  const [shippingPrice, setShippingPrice] = useState(cloneSource?.shipping_price ?? 0);
  const [freightDiscountValue, setFreightDiscountValue] = useState(cloneSource?.freight_discount_value ?? 0);
  const [shippingMethod, setShippingMethod] = useState(cloneSource?.shipping_chosen_by_customer ?? '');
  const [deliveryTime, setDeliveryTime] = useState<number | ''>(cloneSource?.delivery_time_chosen_by_customer ?? '');
  const [paymentMethod, setPaymentMethod] = useState(cloneSource?.payment_method_description ?? '');
  const [orderDiscount, setOrderDiscount] = useState(cloneSource?.order_discount ?? 0);

  // ── Items (inline) ────────────────────────────────────────────
  const [items, setItems] = useState<FormItem[]>(() => {
    if (cloneSource?.items?.length) {
      return cloneSource.items.map(i => ({
        sku: i.sku,
        description: i.description,
        quantity: i.quantity,
        unit_price: i.unit_price,
        unit_cost: i.unit_cost ?? 0,
        discount: i.discount ?? 0,
        _tempId: nextTempId(),
      }));
    }
    return [];
  });
  const [itemShowNew, setItemShowNew] = useState(false);
  const [newItemForm, setNewItemForm] = useState<Partial<ItemPayload>>({ sku: '', description: '', quantity: 1, unit_price: 0, unit_cost: 0, discount: 0 });
  // Product search for new item row
  const [itemProductQuery, setItemProductQuery] = useState('');
  const [itemProductResults, setItemProductResults] = useState<Product[]>([]);
  const [itemProductLoading, setItemProductLoading] = useState(false);
  const [itemProductOpen, setItemProductOpen] = useState(false);
  const itemProductDebounce = useRef<ReturnType<typeof setTimeout>>();
  const [itemSelectedStockAvailable, setItemSelectedStockAvailable] = useState<number | null>(null);

  // ── Installments (inline) ─────────────────────────────────────
  const [installments, setInstallments] = useState<FormInstallment[]>(() => {
    if (cloneSource?.installments?.length) {
      return cloneSource.installments.map(i => ({
        value: i.value,
        due_date: i.due_date,
        notes: i.notes ?? '',
        payment_method_id: i.payment_method_id,
        payment_method_name: i.payment_method_name ?? '',
        _tempId: nextTempId(),
      }));
    }
    return [];
  });
  const [instShowNew, setInstShowNew] = useState(false);
  const [newInstForm, setNewInstForm] = useState<Partial<InstallmentPayload>>({ value: 0, due_date: new Date().toISOString().slice(0, 10), notes: '', payment_method_id: undefined, payment_method_name: '' });

  // ── Payment methods ─────────────────────────────────────────────────
  const [paymentMethods, setPaymentMethods] = useState<OrderPaymentMethod[]>([]);

  // ── Sellers ────────────────────────────────────────────────────────
  const [sellers, setSellers] = useState<Seller[]>([]);

  // ── Status options (loaded from API) ──────────────────────────
  const [manualStatusOpts, setManualStatusOpts] = useState(() =>
    toManualStatusOptions(SALES_ORDER_MANUAL_STATUS_OPTIONS.map(s => ({
      order_status_id: s.id,
      status_name: s.value,
      can_user_choose_manually: true,
      block_order_change: false,
      allocate_available_stock: false,
    }))),
  );

  // ── Load lookups ──────────────────────────────────────────────
  useEffect(() => {
    companyService.getCompanies()
      .then(list => {
        setCompanies(list);
        if (!cloneSource && list.length === 1) setCompanyId(list[0].company_id);
      })
      .catch(() => {});
    salesOrderService.getPaymentMethods().then(setPaymentMethods).catch(() => {});
    salesOrderService.getSellers().then(setSellers).catch(() => {});
    salesOrderService.getOrderStatuses().then(data => {
      const opts = toManualStatusOptions(data);
      if (opts.length > 0) setManualStatusOpts(opts);
    }).catch(() => {});
    orderTypeService.listAll().then(setOrderTypes).catch(() => {});
    // Carga única das listas ao montar; cloneSource vem do state da navegação e não muda
    // durante a vida da tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── CEP: preenchimento + conferência ───────────────────────────
  // [Financial].[Order].[ShipAddrZip] é nvarchar(8): o CEP viaja sem máscara.
  // Deixar o hífen passar produzia 9 caracteres e um 400 do backend.
  const handleCepChange = (value: string) => {
    setAddrZip(normalizeCep(value));
  };

  // A consulta do CEP roda sozinha (CEP digitado ou vindo do cliente), completa só os campos
  // vazios e compara o restante: um CEP de outra cidade (pedido 29746: CEP da Bahia com
  // endereço de São Paulo) vira aviso na tela em vez de ser gravado em silêncio.
  const addrCheck = useAddressCepCheck(
    { zip: addrZip, uf: addrState, city: addrCity, district: addrDistrict, street: addrStreet },
    {
      onFound: cep => {
        setAddrStreet(v => (v.trim() ? v : cep.street));
        setAddrDistrict(v => (v.trim() ? v : cep.district));
        setAddrCity(v => (v.trim() ? v : cep.city));
        setAddrState(v => (v.trim() ? v : cep.uf));
      },
    },
  );
  const [cepDivergenceConfirmOpen, setCepDivergenceConfirmOpen] = useState(false);

  const applyCepAddress = () => {
    if (!addrCheck.cepAddress) return;
    const next = applyCepToDivergent(
      { uf: addrState, city: addrCity, district: addrDistrict, street: addrStreet },
      addrCheck.cepAddress,
      addrCheck.divergences,
    );
    setAddrState(next.uf);
    setAddrCity(next.city);
    setAddrDistrict(next.district);
    setAddrStreet(next.street);
  };

  // ── Customer search via modal ──────────────────────────────────

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    // Only fill address if not already filled
    // Always reset address and fill with the new customer's default address
    const addr = customer.default_address;
    setAddrStreet(addr?.addr_street || '');
    // CustomerAddr.AddrNum é nvarchar(20), mas Order.ShipAddrNum é nvarchar(10).
    // Trunca no preenchimento para o usuário ver e ajustar o valor antes de
    // salvar, em vez de levar um 400 só ao clicar em salvar.
    setAddrNum((addr?.addr_num || '').slice(0, 10));
    setAddrCompl(addr?.addr_compl || '');
    setAddrDistrict(addr?.addr_district || '');
    setAddrCity(addr?.addr_city || '');
    setAddrState(addr?.addr_state || '');
    setAddrZip((addr?.addr_zip || '').replace(/\D/g, '').slice(0, 8));
  };

  // ── Product search for items ──────────────────────────────────

  const handleItemProductSearch = (query: string) => {
    setItemProductQuery(query);
    setNewItemForm(f => ({ ...f, sku: '', description: '' }));
    clearTimeout(itemProductDebounce.current);
    if (!query.trim()) {
      setItemProductResults([]);
      setItemProductOpen(false);
      return;
    }
    setItemProductOpen(true);
    setItemProductLoading(true);
    itemProductDebounce.current = setTimeout(async () => {
      try {
        const result = await productService.searchProducts(query.trim(), 1, 10, { active: true });
        setItemProductResults(result.products || []);
      } catch {
        setItemProductResults([]);
      } finally {
        setItemProductLoading(false);
      }
    }, 300);
  };

  const handleItemProductSelect = (product: Product) => {
    setNewItemForm(f => ({
      ...f,
      sku: product.sku,
      description: product.description,
      unit_price: product.price ?? f.unit_price,
      unit_cost: product.cost ?? f.unit_cost,
    }));
    setItemSelectedStockAvailable(product.stock_quantity_available ?? null);
    setItemProductQuery(product.description);
    setItemProductOpen(false);
    setItemProductResults([]);
  };

  const addItem = () => {
    if (!newItemForm.sku) {
      setError('Selecione um produto da lista antes de adicionar.');
      return;
    }
    const isGift = !newItemForm.unit_price || newItemForm.unit_price === 0;
    setItems(prev => [...prev, { ...(newItemForm as ItemPayload), is_gift: isGift, _tempId: nextTempId() }]);
    setNewItemForm({ sku: '', description: '', quantity: 1, unit_price: 0, unit_cost: 0, discount: 0 });
    setItemProductQuery('');
    setItemShowNew(false);
    setItemSelectedStockAvailable(null);
    setError(null);
  };

  const removeItem = (tempId: number) => {
    setItems(prev => prev.filter(i => i._tempId !== tempId));
  };

  // ── Installments ──────────────────────────────────────────────

  const addInstallment = () => {
    setInstallments(prev => [...prev, { ...(newInstForm as InstallmentPayload), _tempId: nextTempId() }]);
    setNewInstForm({ value: 0, due_date: new Date().toISOString().slice(0, 10), notes: '', payment_method_id: undefined, payment_method_name: '' });
    setInstShowNew(false);
  };

  const removeInstallment = (tempId: number) => {
    setInstallments(prev => prev.filter(i => i._tempId !== tempId));
  };

  // ── Computed totals ───────────────────────────────────────────
  // UnitPrice é o valor original do item; discount é percentual a ser aplicado
  const itemsTotal = items.reduce((s, i) => {
    const price = i.unit_price || 0;
    const discountPercent = i.discount || 0;
    const discountedPrice = price * (1 - discountPercent / 100);
    return s + discountedPrice * i.quantity;
  }, 0);
  const orderTotal = itemsTotal + shippingPrice - Math.min(freightDiscountValue, shippingPrice) - orderDiscount;
  const installmentsTotal = installments.reduce((s, i) => s + i.value, 0);
  const selectedOrderType = orderTypes.find(t => t.order_type_id === orderTypeId);
  const isSelectedOrderTypeFullGift = Boolean(
    selectedOrderType?.generate_invoice && !selectedOrderType?.has_financial_value,
  );

  // ── Save ──────────────────────────────────────────────────────

  const handleSave = async (cepDivergenceConfirmed = false) => {
    if (!companyId) { setError('Selecione a empresa.'); return; }
    if (!orderDate) { setError('Informe a data do pedido.'); return; }
    if (!orderTypeId) { setError('Selecione o tipo do pedido.'); return; }
    if (items.length === 0) { setError('Adicione pelo menos um item ao pedido.'); return; }
    // Endereço de entrega é o que faz o pedido ser despachável: sem ele não há cotação de frete
    // nem NF-e, e a falta só aparece dias depois, na expedição. Mesma regra do backend.
    const addrError = shippingAddressError({
      ship_addr_street: addrStreet,
      ship_addr_num: addrNum,
      ship_addr_district: addrDistrict,
      ship_addr_city: addrCity,
      ship_addr_state: addrState,
      ship_addr_zip: addrZip,
    });
    if (addrError) { setError(addrError); return; }
    // Divergência entre endereço e CEP não bloqueia (a base de CEP pode estar desatualizada),
    // mas exige confirmação explícita.
    if (!cepDivergenceConfirmed && addrCheck.divergences.length > 0) {
      setCepDivergenceConfirmOpen(true);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const created = await salesOrderService.createOrder({
        company_id: companyId as number,
        order_date: orderDate,
        order_status_id: orderStatus,
        order_type_id: orderTypeId as number,
        customer_id: selectedCustomer?.customer_id,
      customer_name: selectedCustomer?.name || undefined,
        seller: seller || undefined,
        observation: observation || undefined,
        internal_observation: internalObs || undefined,
        is_priority: isPriority || undefined,
        request_delivery_time: requestDeliveryTime || undefined,
        shipping_price: shippingPrice || undefined,
        freight_discount_value: freightDiscountValue || undefined,
        shipping_chosen_by_customer: shippingMethod || undefined,
        delivery_time_chosen_by_customer: deliveryTime !== '' ? deliveryTime : undefined,
        payment_method_description: paymentMethod || undefined,
        order_discount: orderDiscount || undefined,
        ship_addr_street: addrStreet || undefined,
        ship_addr_num: addrNum || undefined,
        ship_addr_compl: addrCompl || undefined,
        ship_addr_district: addrDistrict || undefined,
        ship_addr_city: addrCity || undefined,
        ship_addr_state: addrState || undefined,
        ship_addr_zip: addrZip || undefined,
        items: items.map(({ _tempId, is_gift, ...rest }) => ({ ...rest, is_gift: is_gift ?? false })),
        installments: installments.map(({ _tempId, ...rest }) => rest),
      });
      navigate(`/sales-orders/${created.order_id}`);
    } catch (err) {
      setError(err ?? 'Erro ao criar pedido');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/sales-orders')}><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {isClone ? `Clonar Pedido #${cloneSource!.order_id}` : 'Novo Pedido de Venda'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isClone
                ? 'Revise os dados clonados e faça as alterações necessárias antes de salvar'
                : 'Preencha os dados e adicione os itens'}
            </p>
          </div>
        </div>
        <Button icon={<Save size={18} />} loading={saving} onClick={() => handleSave()}>
          {isClone ? 'Salvar Clone' : 'Salvar Pedido'}
        </Button>
      </div>

      <ApiErrorModal error={error} onClose={() => setError(null)} />

      <ConfirmDialog
        isOpen={cepDivergenceConfirmOpen}
        onClose={() => setCepDivergenceConfirmOpen(false)}
        onConfirm={() => {
          setCepDivergenceConfirmOpen(false);
          void handleSave(true);
        }}
        title="Endereço não confere com o CEP"
        message="O endereço de entrega não confere com o CEP informado. Salvar mesmo assim?"
        confirmText="Salvar mesmo assim"
        cancelText="Revisar endereço"
        severity="warning"
      />

      {isClone && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-center gap-2">
          <Copy size={16} className="text-blue-600 shrink-0" />
          <span>Clonando pedido <span className="font-semibold">#{cloneSource!.order_id}</span>. Revise os dados e clique em &quot;Salvar Clone&quot; para criar o novo pedido.</span>
        </div>
      )}

      {/* Dados Gerais */}
      <Card>
        <CardHeader title="Dados Gerais" />
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Empresa *</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={companyId}
                onChange={e => setCompanyId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Selecione...</option>
                {companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>{c.name} — {formatCnpj(c.document)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Data do Pedido *</label>
              <DateInputBR
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={orderDate}
                onChange={v => setOrderDate(v)}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Status</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={orderStatus}
                onChange={e => setOrderStatus(Number(e.target.value))}
              >
                {manualStatusOpts.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Tipo de Pedido *</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={orderTypeId}
                onChange={e => setOrderTypeId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Selecione...</option>
                {orderTypes.map(t => (
                  <option key={t.order_type_id} value={t.order_type_id}>{t.order_type}</option>
                ))}
              </select>
              {isSelectedOrderTypeFullGift && (
                <p className="mt-1 text-xs text-pink-700">
                  Tipo selecionado define pedido presente integral: na emissão fiscal deve ser gerada apenas NF de bonificação.
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Vendedor</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={seller}
                onChange={e => setSeller(e.target.value)}
              >
                <option value="">Selecione...</option>
                {sellers.map(s => (
                  <option key={s.seller_id} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Forma de Pagamento</label>
              <select
                className="mt-1 w-full input-field px-3 py-2 text-sm"
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
              >
                <option value="">Selecione...</option>
                {PAYMENT_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-full">
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Observação</label>
              <textarea
                rows={2}
                className="mt-1 w-full input-field px-3 py-2 text-sm resize-none"
                placeholder="Observações visíveis ao cliente"
                value={observation}
                onChange={e => setObservation(e.target.value)}
              />
            </div>
            <div className="sm:col-span-full">
              <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Observação Interna</label>
              <textarea
                rows={2}
                className="mt-1 w-full input-field px-3 py-2 text-sm resize-none"
                placeholder="Observações internas (não visíveis ao cliente)"
                value={internalObs}
                onChange={e => setInternalObs(e.target.value)}
              />
            </div>
            {/* Prioridade e Horário requisitado */}
            <div className="flex items-center gap-3 sm:col-span-full">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPriority}
                  onChange={e => setIsPriority(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-xs text-slate-700 font-medium uppercase tracking-wider">Pedido Prioritário</span>
              </label>
            </div>
            {isPriority && (
              <div className="sm:col-span-2">
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Horário de Entrega Requisitado</label>
                <input
                  type="datetime-local"
                  className="mt-1 w-full input-field px-3 py-2 text-sm"
                  value={requestDeliveryTime}
                  onChange={e => setRequestDeliveryTime(e.target.value)}
                />
                <p className="text-xs text-slate-400 mt-0.5">Opcional — se não informado, apenas a flag de prioridade será considerada</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cliente */}
      <Card>
        <CardHeader title="Cliente" />
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              {selectedCustomer ? (
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm">{selectedCustomer.name || '—'}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                      {selectedCustomer.document && (
                        <span className="text-xs text-slate-500">{formatDocument(selectedCustomer.document)}</span>
                      )}
                      {selectedCustomer.email && (
                        <span className="text-xs text-slate-500">{selectedCustomer.email}</span>
                      )}
                      {selectedCustomer.cell_phone && (
                        <span className="text-xs text-slate-500">{selectedCustomer.cell_phone}</span>
                      )}
                    </div>
                  </div>
                  <button
                    className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded"
                    onClick={() => setSelectedCustomer(null)}
                    title="Remover cliente"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-400">Nenhum cliente selecionado</p>
              )}
            </div>
            <Button variant="outline" size="sm" icon={<Search size={14} />} onClick={() => setCustomerSearchOpen(true)}>
              Buscar Cliente
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Customer search modal */}
      <CustomerSearchModal
        isOpen={customerSearchOpen}
        onClose={() => setCustomerSearchOpen(false)}
        onSelect={handleCustomerSelect}
      />

      {/* Endereço de Entrega + Frete */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Endereço de Entrega" />
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">CEP</label>
                <div className="mt-1 relative">
                  <input
                    className="w-full input-field px-3 py-2 text-sm pr-8"
                    placeholder="00000000"
                    inputMode="numeric"
                    maxLength={8}
                    value={addrZip}
                    onChange={e => handleCepChange(e.target.value)}
                  />
                  {addrCheck.status === 'loading' && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
                </div>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Logradouro</label>
                <input className="mt-1 w-full input-field px-3 py-2 text-sm" placeholder="Rua / Av." value={addrStreet} onChange={e => setAddrStreet(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Número</label>
                {/* ShipAddrNum é nvarchar(10) — complemento vai no campo ao lado. */}
                <input className="mt-1 w-full input-field px-3 py-2 text-sm" maxLength={10} value={addrNum} onChange={e => setAddrNum(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Complemento</label>
                <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrCompl} onChange={e => setAddrCompl(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Bairro</label>
                <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrDistrict} onChange={e => setAddrDistrict(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Cidade</label>
                <input className="mt-1 w-full input-field px-3 py-2 text-sm" value={addrCity} onChange={e => setAddrCity(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">UF</label>
                <input className="mt-1 w-full input-field px-3 py-2 text-sm" placeholder="SP" maxLength={2} value={addrState} onChange={e => setAddrState(e.target.value.toUpperCase())} />
              </div>
            </div>
            <AddressCepNotice check={addrCheck} onApplyCep={applyCepAddress} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Frete e Envio" />
          <CardContent>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Modalidade de Frete</label>
                <input className="mt-1 w-full input-field px-3 py-2 text-sm" placeholder="Ex: SEDEX, PAC, Frete Grátis..." value={shippingMethod} onChange={e => setShippingMethod(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Valor do Frete (R$)</label>
                <input type="number" min="0" step="0.01" className="mt-1 w-full input-field px-3 py-2 text-sm" value={shippingPrice} onChange={e => setShippingPrice(parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Desconto no Frete (R$)</label>
                <input type="number" min="0" step="0.01" className="mt-1 w-full input-field px-3 py-2 text-sm" value={freightDiscountValue} onChange={e => setFreightDiscountValue(parseFloat(e.target.value) || 0)} />
                {freightDiscountValue > shippingPrice && (
                  <p className="mt-1 text-xs text-red-500">Desconto não pode ser superior ao valor do frete</p>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Desconto do Pedido (R$)</label>
                <input type="number" min="0" step="0.01" className="mt-1 w-full input-field px-3 py-2 text-sm" value={orderDiscount} onChange={e => setOrderDiscount(parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Prazo de Entrega (dias)</label>
                <input type="number" min="0" className="mt-1 w-full input-field px-3 py-2 text-sm" value={deliveryTime} onChange={e => setDeliveryTime(e.target.value ? parseInt(e.target.value) : '')} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Itens */}
      <Card>
        <CardHeader
          title="Itens do Pedido"
          action={
            !itemShowNew
              ? <button className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors font-medium" onClick={() => setItemShowNew(true)}>
                  <Plus size={12} /> Adicionar Item
                </button>
              : undefined
          }
        />
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">SKU</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Qtd</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor Unit.</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Desconto (%)</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                  <th className="py-3 px-4 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item._tempId} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-4 font-medium text-slate-900 text-sm">{item.sku}</td>
                    <td className="py-2 px-4 text-slate-600 text-sm">{item.description || '—'}</td>
                    <td className="py-2 px-4 text-right text-sm">{item.quantity}</td>
                    <td className="py-2 px-4 text-right text-sm">{formatCurrency(item.unit_price)}</td>
                    <td className="py-2 px-4 text-right text-sm">{item.discount ? `${item.discount}%` : '—'}</td>
                    <td className="py-2 px-4 text-right font-medium text-sm">
                      {formatCurrency((item.unit_price || 0) * (1 - (item.discount || 0) / 100) * item.quantity)}
                    </td>
                    <td className="py-2 px-3">
                      <button className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded" onClick={() => removeItem(item._tempId)}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}

                {/* New item row */}
                {itemShowNew && (
                  <tr className="border-b border-emerald-100 bg-emerald-50">
                    <td className="py-2 px-4 text-sm text-slate-500 font-medium">{newItemForm.sku || '—'}</td>
                    <td className="py-2 px-4 relative min-w-[220px]">
                      <input
                        type="text"
                        className="w-full border border-slate-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="Buscar produto por nome ou SKU..."
                        value={itemProductQuery}
                        onChange={e => handleItemProductSearch(e.target.value)}
                        onFocus={() => { if (itemProductQuery.trim()) setItemProductOpen(true); }}
                        onBlur={() => setTimeout(() => setItemProductOpen(false), 150)}
                        autoFocus
                      />
                      {itemProductOpen && (
                        <div className="absolute left-0 top-full z-50 bg-white border border-slate-200 rounded shadow-lg w-80 max-h-48 overflow-y-auto">
                          {itemProductLoading
                            ? <div className="p-3 text-sm text-slate-400 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Buscando...</div>
                            : itemProductResults.length === 0
                              ? <div className="p-3 text-sm text-slate-400 text-center">Nenhum produto encontrado</div>
                              : itemProductResults.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-slate-100 last:border-0"
                                  onMouseDown={() => handleItemProductSelect(p)}
                                >
                                  <span className="font-medium text-slate-900">{p.sku}</span>
                                  <span className="text-slate-500 ml-2 text-xs">{p.description}</span>
                                  {p.stock_quantity_available != null && (
                                    <span className={`ml-2 text-xs font-medium ${p.stock_quantity_available < 0 ? 'text-red-600' : p.stock_quantity_available === 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                      | Disp: {p.stock_quantity_available}
                                    </span>
                                  )}
                                </button>
                              ))
                          }
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <input type="number" min="1" className="w-16 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newItemForm.quantity ?? 1} onChange={e => setNewItemForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} />
                      {itemSelectedStockAvailable !== null && (newItemForm.quantity ?? 1) > itemSelectedStockAvailable && (
                        <p className="text-xs text-amber-600 mt-0.5" title="O pedido será criado, mas pode haver atraso na entrega.">
                          ⚠️ Excede disp. ({itemSelectedStockAvailable})
                        </p>
                      )}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <input type="number" min="0" step="0.01" className="w-24 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newItemForm.unit_price ?? 0} onChange={e => setNewItemForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))} />
                    </td>
                    <td className="py-2 px-4 text-right">
                      <input type="number" min="0" step="0.01" className="w-20 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newItemForm.discount ?? 0} onChange={e => setNewItemForm(f => ({ ...f, discount: parseFloat(e.target.value) || 0 }))} />
                    </td>
                    <td className="py-2 px-4 text-right text-slate-400 text-sm">—</td>
                    <td className="py-2 px-3">
                      <div className="flex gap-0.5">
                        <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Adicionar" onClick={addItem}>
                          <Save size={13} />
                        </button>
                        <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={() => { setItemShowNew(false); setItemProductQuery(''); setItemProductResults([]); setNewItemForm({ sku: '', description: '', quantity: 1, unit_price: 0, unit_cost: 0, discount: 0 }); setItemSelectedStockAvailable(null); }}>
                          <X size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {items.length === 0 && !itemShowNew && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-400 text-sm">Nenhum item adicionado. Clique em "Adicionar Item".</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr>
                  <td colSpan={5} className="py-3 px-4 text-right text-sm font-semibold text-slate-700 uppercase">Subtotal Produtos</td>
                  <td className="py-3 px-4 text-right font-bold text-slate-900">{formatCurrency(itemsTotal)}</td>
                  <td />
                </tr>
                <tr>
                  <td colSpan={5} className="py-1 px-4 text-right text-sm text-slate-600">Frete</td>
                  <td className="py-1 px-4 text-right text-slate-900">{formatCurrency(shippingPrice)}</td>
                  <td />
                </tr>
                {freightDiscountValue > 0 && (
                  <tr>
                    <td colSpan={5} className="py-1 px-4 text-right text-sm text-slate-600">Desconto no Frete</td>
                    <td className="py-1 px-4 text-right text-red-600">- {formatCurrency(Math.min(freightDiscountValue, shippingPrice))}</td>
                    <td />
                  </tr>
                )}
                <tr>
                  <td colSpan={5} className="py-1 px-4 text-right text-sm text-slate-600">Desconto</td>
                  <td className="py-1 px-4 text-right text-red-600">- {formatCurrency(orderDiscount)}</td>
                  <td />
                </tr>
                <tr className="border-t border-slate-300">
                  <td colSpan={5} className="py-3 px-4 text-right font-bold text-slate-900 uppercase text-sm">Total Geral</td>
                  <td className="py-3 px-4 text-right font-bold text-emerald-700 text-base">{formatCurrency(orderTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Parcelas */}
      <Card>
        <CardHeader
          title="Parcelas"
          action={
            !instShowNew
              ? <button className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors font-medium" onClick={() => setInstShowNew(true)}>
                  <Plus size={12} /> Adicionar Parcela
                </button>
              : undefined
          }
        />
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Vencimento</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Forma de Pgto.</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Observação</th>
                  <th className="py-3 px-4 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {installments.map((inst, idx) => (
                  <tr key={inst._tempId} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-4 font-medium text-slate-900 text-sm">{idx + 1}</td>
                    <td className="py-2 px-4 text-sm">{inst.due_date}</td>
                    <td className="py-2 px-4 text-right font-medium text-sm">{formatCurrency(inst.value)}</td>
                    <td className="py-2 px-4 text-slate-600 text-sm">{inst.payment_method_name || '—'}</td>
                    <td className="py-2 px-4 text-slate-600 text-sm">{inst.notes || '—'}</td>
                    <td className="py-2 px-3">
                      <button className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded" onClick={() => removeInstallment(inst._tempId)}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}

                {/* New installment row */}
                {instShowNew && (
                  <tr className="border-b border-emerald-100 bg-emerald-50">
                    <td className="py-2 px-4 text-slate-400">—</td>
                    <td className="py-2 px-4">
                      <DateInputBR className="border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newInstForm.due_date ?? ''} onChange={v => setNewInstForm(f => ({ ...f, due_date: v }))} autoFocus />
                    </td>
                    <td className="py-2 px-4 text-right">
                      <input type="number" min="0" step="0.01" className="w-28 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newInstForm.value ?? 0} onChange={e => setNewInstForm(f => ({ ...f, value: parseFloat(e.target.value) || 0 }))} />
                    </td>
                    <td className="py-2 px-4">
                      <select className="border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newInstForm.payment_method_id ?? ''} onChange={e => { const id = parseInt(e.target.value); const pm = paymentMethods.find(p => p.payment_method_id === id); setNewInstForm(f => ({ ...f, payment_method_id: id || undefined, payment_method_name: pm?.payment_method_name ?? '' })); }}>
                        <option value="">-- Selecione --</option>
                        {paymentMethods.map(pm => (
                          <option key={pm.payment_method_id} value={pm.payment_method_id}>{pm.payment_method_name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-4">
                      <input className="border border-slate-300 rounded px-1 py-0.5 text-sm w-32 focus:outline-none focus:ring-1 focus:ring-emerald-500" value={newInstForm.notes ?? ''} onChange={e => setNewInstForm(f => ({ ...f, notes: e.target.value }))} placeholder="Obs." />
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex gap-0.5">
                        <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Adicionar" onClick={addInstallment}>
                          <Save size={13} />
                        </button>
                        <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={() => { setInstShowNew(false); setNewInstForm({ value: 0, due_date: new Date().toISOString().slice(0, 10), notes: '', payment_method_id: undefined, payment_method_name: '' }); }}>
                          <X size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {installments.length === 0 && !instShowNew && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-400 text-sm">Nenhuma parcela adicionada.</td>
                  </tr>
                )}
              </tbody>
              {installments.length > 0 && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={2} className="py-3 px-4 text-right font-semibold text-slate-700 text-sm">Total Parcelas</td>
                    <td className="py-3 px-4 text-right font-bold text-slate-900 text-sm">{formatCurrency(installmentsTotal)}</td>
                    <td colSpan={3}></td>
                  </tr>
                  {Math.abs(installmentsTotal - orderTotal) > 0.01 && (
                    <tr>
                      <td colSpan={6} className="py-2 px-4 text-center text-xs text-amber-600">
                        ⚠ Total das parcelas ({formatCurrency(installmentsTotal)}) difere do total do pedido ({formatCurrency(orderTotal)})
                      </td>
                    </tr>
                  )}
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Bottom save */}
      <div className="flex justify-end gap-3 pb-4">
        <Button variant="outline" onClick={() => navigate('/sales-orders')}>Cancelar</Button>
        <Button icon={<Save size={18} />} loading={saving} onClick={() => handleSave()}>
          {isClone ? 'Salvar Clone' : 'Salvar Pedido'}
        </Button>
      </div>
    </div>
  );
};
